import { useEffect, useRef } from 'react'
import './ComposerTools.css'
import { EmojiPickerPanel } from '../../components/EmojiPickerPanel'

// EmojiPicker is the full emoji picker (every emoji, search by name/keyword,
// skin tones, recents — see EmojiPickerPanel) in the app's frame. Selecting an
// emoji calls onPick; the parent owns what to do with it (composer caret
// insertion, or a reaction). Default is the composer's floating popover;
// `inline` drops the absolute positioning + chrome so it can sit inside another
// panel (the reaction menu).
//
// (It used to be a hand-picked list of ~200 emoji. The full set with search is
// what people expect from iMessage/Instagram, and there's no native picker a
// WKWebView app can open — see the PR that replaced it.)
//
// onClose (the floating popover) makes it dismissible like any popover: Escape,
// or a press anywhere outside it. A press on an element marked
// data-emoji-toggle (the button that opens it) is left to that button, so
// clicking it still toggles rather than closing-then-reopening.
export function EmojiPicker({
  onPick,
  inline,
  onClose,
}: {
  onPick: (emoji: string) => void
  inline?: boolean
  onClose?: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!onClose) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onPointer = (e: PointerEvent) => {
      const el = ref.current
      // composedPath reaches into the picker's shadow DOM, where the real
      // target of a press inside it lives.
      const path = e.composedPath()
      if (el && path.includes(el)) return
      if (path.some((n) => n instanceof Element && n.hasAttribute('data-emoji-toggle'))) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer, true)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer, true)
    }
  }, [onClose])
  return (
    <div ref={ref} className={`emoji-picker${inline ? ' emoji-picker--inline' : ''}`}>
      <EmojiPickerPanel onPick={onPick} />
    </div>
  )
}

export type FormatKind = 'bold' | 'italic' | 'strike' | 'code' | 'link'

// Result of a composer text edit: the new value plus where the selection should
// land afterward. Pure so it's unit-testable independent of the DOM textarea.
export type SelEdit = { value: string; caretStart: number; caretEnd: number }

// formatSelection applies a markdown format to value[start..end]. With a
// selection it wraps and keeps it selected inside the markers; with an empty
// selection it drops the caret between them (link is special: it selects the
// "url" placeholder to type over).
export function formatSelection(
  kind: FormatKind,
  value: string,
  start: number,
  end: number,
): SelEdit {
  const wrap = (before: string, after: string): SelEdit => {
    const selected = value.slice(start, end)
    const next = value.slice(0, start) + before + selected + after + value.slice(end)
    if (selected) {
      return { value: next, caretStart: start + before.length, caretEnd: end + before.length }
    }
    const caret = start + before.length
    return { value: next, caretStart: caret, caretEnd: caret }
  }
  switch (kind) {
    case 'bold':
      return wrap('**', '**')
    case 'italic':
      return wrap('_', '_')
    case 'strike':
      return wrap('~~', '~~')
    case 'code':
      return wrap('`', '`')
    case 'link': {
      const label = value.slice(start, end) || 'text'
      const inserted = `[${label}](url)`
      const next = value.slice(0, start) + inserted + value.slice(end)
      const urlStart = start + label.length + 3 // past "[label]("
      return { value: next, caretStart: urlStart, caretEnd: urlStart + 3 } // select "url"
    }
  }
}

// insertAtSelection replaces value[start..end] with str and puts the caret after
// it. Used for emoji insertion.
export function insertAtSelection(value: string, start: number, end: number, str: string): SelEdit {
  const next = value.slice(0, start) + str + value.slice(end)
  const caret = start + str.length
  return { value: next, caretStart: caret, caretEnd: caret }
}

const FORMAT_BUTTONS: { kind: FormatKind; glyph: string; label: string }[] = [
  { kind: 'bold', glyph: 'B', label: 'Bold' },
  { kind: 'italic', glyph: 'I', label: 'Italic' },
  { kind: 'strike', glyph: 'S', label: 'Strikethrough' },
  { kind: 'code', glyph: '</>', label: 'Code' },
  { kind: 'link', glyph: '🔗', label: 'Link' },
]

// FormatBar renders the markdown-formatting buttons (each wraps the composer's
// current selection; the parent owns the text mutation) plus an optional
// Preview toggle that swaps the textarea for a rendered view of the draft.
export function FormatBar({
  onFormat,
  previewActive,
  onTogglePreview,
}: {
  onFormat: (kind: FormatKind) => void
  previewActive?: boolean
  onTogglePreview?: () => void
}) {
  return (
    <div className="format-bar" role="toolbar" aria-label="Text formatting">
      {FORMAT_BUTTONS.map((b) => (
        <button
          key={b.kind}
          type="button"
          className={`format-bar__btn format-bar__btn--${b.kind}`}
          // Preserve the textarea selection through the tap.
          onMouseDown={(ev) => {
            ev.preventDefault()
            onFormat(b.kind)
          }}
          aria-label={b.label}
          title={b.label}
          disabled={previewActive}
        >
          {b.glyph}
        </button>
      ))}
      {onTogglePreview && (
        <button
          type="button"
          className={`format-bar__btn format-bar__preview${previewActive ? ' format-bar__preview--active' : ''}`}
          onMouseDown={(ev) => {
            ev.preventDefault()
            onTogglePreview()
          }}
          aria-pressed={previewActive}
          title="Toggle preview"
        >
          {previewActive ? 'Edit' : 'Preview'}
        </button>
      )}
    </div>
  )
}
